/**
 * Resume Generation Section
 * Sub-component of PreferencesModal for skillsnap URL toggle, show year checkboxes, and cutoff year select.
 */
import type { UserPreferences } from '../../types';

export interface YearLimitsSectionProps {
  preferences: UserPreferences;
  onPreferencesChange: (updated: UserPreferences) => void;
}

export function YearLimitsSection({ preferences, onPreferencesChange }: YearLimitsSectionProps) {
  // Generate year options (current year down to 1970)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1969 }, (_, i) => currentYear - i);

  const isTrial = preferences.status === 'trial';

  return (
    <div className="p-3 space-y-3">
      {/* --- Resume Output Options --- */}
      {/* Add SkillSnap Watermark */}
      <label className={`flex items-center justify-between p-2 bg-gray-50 rounded-lg ${isTrial ? 'opacity-70' : 'cursor-pointer'}`}>
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Add SkillSnap watermark to generated files</span>
          {isTrial && (
            <span className="text-xs text-gray-500">(Required for trial accounts)</span>
          )}
        </div>
        <input
          type="checkbox"
          checked={isTrial ? true : (preferences.add_skillsnap_watermark !== false)}
          disabled={isTrial}
          onChange={(e) => onPreferencesChange({ ...preferences, add_skillsnap_watermark: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 disabled:opacity-50"
        />
      </label>

      {/* Auto-download files when opened */}
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Auto-save Resume & Cover Letter when opened</span>
          <span className="text-xs text-gray-500">Saves a copy to your Downloads folder</span>
        </div>
        <input
          type="checkbox"
          checked={preferences.auto_download_files !== false}
          onChange={(e) => onPreferencesChange({ ...preferences, auto_download_files: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
      </label>

      {/* Link Name to URL */}
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
        <span className="text-sm text-gray-700">Link resume name to your public profile</span>
        <input
          type="checkbox"
          checked={preferences.link_name_to_url || false}
          onChange={(e) => onPreferencesChange({ ...preferences, link_name_to_url: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
      </label>

      {/* Separator between output options and date filter checkboxes */}
      <hr className="border-gray-300 my-3" />

      {/* SkillSnap URL on Resumes */}
      <label className={`flex items-center justify-between p-2 bg-gray-50 rounded-lg ${isTrial ? 'opacity-70' : 'cursor-pointer'}`}>
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Link your_name.skillsnap.me on bottom of Resumes</span>
          {isTrial && (
            <span className="text-xs text-gray-500">Upgrade to remove this</span>
          )}
        </div>
        <input
          type="checkbox"
          checked={isTrial ? true : (preferences.show_skillsnap_url !== false)}
          disabled={isTrial}
          onChange={(e) => onPreferencesChange({ ...preferences, show_skillsnap_url: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 disabled:opacity-50"
        />
      </label>

      {/* Show Year for Education */}
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
        <span className="text-sm text-gray-700">Show Year for Education Items</span>
        <input
          type="checkbox"
          checked={preferences.show_year_education || false}
          onChange={(e) => onPreferencesChange({ ...preferences, show_year_education: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
      </label>

      {/* Show Year for Awards */}
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
        <span className="text-sm text-gray-700">Show Year for Award Items</span>
        <input
          type="checkbox"
          checked={preferences.show_year_awards || false}
          onChange={(e) => onPreferencesChange({ ...preferences, show_year_awards: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
      </label>

      {/* Show Year for Presentations */}
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
        <span className="text-sm text-gray-700">Show Year for Presentation Items</span>
        <input
          type="checkbox"
          checked={preferences.show_year_presentations || false}
          onChange={(e) => onPreferencesChange({ ...preferences, show_year_presentations: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
      </label>

      {/* Bullet Period Style */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <span className="text-sm text-gray-700">For each Bullet in Experience section,</span>
        <select
          value={preferences.bullet_period_style || 'none'}
          onChange={(e) => onPreferencesChange({
            ...preferences,
            bullet_period_style: e.target.value
          })}
          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">Do nothing; do not modify Bullets</option>
          <option value="add">Ensure all Bullets end with Period</option>
          <option value="remove">Ensure all Bullets do NOT have a period</option>
        </select>
      </div>

      {/* Target Pages */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Target PDF page count</span>
          <span className="text-xs text-gray-500">Auto-prunes least relevant content to fit</span>
        </div>
        <select
          value={preferences.target_pages ?? ''}
          onChange={(e) => onPreferencesChange({
            ...preferences,
            target_pages: e.target.value ? parseInt(e.target.value) : undefined
          })}
          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">No limit</option>
          <option value="1">1 page</option>
          <option value="2">2 pages</option>
          <option value="3">3 pages</option>
          <option value="4">4 pages</option>
          <option value="5">5 pages</option>
        </select>
      </div>

      {/* Cutoff Year */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <span className="text-sm text-gray-700">Remove data that ended before</span>
        <select
          value={preferences.cutoff_year || ''}
          onChange={(e) => onPreferencesChange({
            ...preferences,
            cutoff_year: e.target.value ? parseInt(e.target.value) : undefined
          })}
          className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">No cutoff</option>
          {yearOptions.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
